<template>
  <div class="container-fluid mt-4">
    <h2 class="mb-2"><i class="bi bi-fire me-2"></i>FIRE</h2>
    <p class="text-muted small mb-3">
      <template v-if="isAdmin">
        El <strong>patrimonio actual</strong> se prerellena desde
        <router-link to="/evolucion">Evolución</router-link> y el
        <strong>gasto mensual objetivo</strong> desde el resumen del
        <router-link to="/resumen">último año natural</router-link>, pero puedes
        ajustar ambos libremente para simular escenarios.
      </template>
      <template v-else>
        Define el patrimonio inicial y el resto de parámetros por defecto en
        <router-link to="/perfil">Editar perfil</router-link>.
      </template>
    </p>

    <div v-if="isAdmin && cargandoExternos" class="text-center py-5">
      <div class="spinner-border text-primary mb-2"></div>
      <p class="text-muted small mb-0">Cargando datos de Evolución y Resumen…</p>
    </div>

    <div v-else-if="isAdmin && errorExternos" class="alert alert-warning small">
      {{ errorExternos }}
    </div>

    <CalculadoraFire
      v-if="!isAdmin || !cargandoExternos"
      :patrimonio-inicial="patrimonioActivo"
      :externo-patrimonio="isAdmin"
      :patrimonio-sugerido-label="isAdmin ? 'Evolución' : ''"
      :gasto-mes-sugerido="gastoMesSugerido"
      :gasto-mes-sugerido-label="gastoMesSugeridoLabel"
      :saved-prefs="firePrefsRaw"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';
import CalculadoraFire from '../components/CalculadoraFire.vue';
import { normalizeFirePrefs } from '../utils/firePrefs';

function parseEuroValor(valor) {
  if (valor == null) return 0;
  const limpio = String(valor).replace(/[^0-9,.\-]/g, '').trim();
  if (!limpio) return 0;
  if (limpio.includes(',')) {
    return parseFloat(limpio.replace(/\./g, '').replace(',', '.')) || 0;
  }
  const partes = limpio.split('.');
  if (partes.length === 2 && partes[1].length === 3) {
    return parseFloat(limpio.replace(/\./g, '')) || 0;
  }
  if (partes.length > 2) {
    return parseFloat(limpio.replace(/\./g, '')) || 0;
  }
  return parseFloat(limpio) || 0;
}

export default {
  name: 'CalculadoraFirePage',
  components: { CalculadoraFire },
  setup() {
    const store = useStore();
    const isAdmin = computed(() => !!store.getters['auth/isAdmin']);
    const patrimonioFetched = ref(0);
    const gastoMesFetched = ref(0);
    const cargandoExternos = ref(false);
    const errorExternos = ref('');

    const firePrefsRaw = computed(() => store.state.auth.user?.fire_prefs ?? null);

    const patrimonioActivo = computed(() => {
      if (isAdmin.value) {
        return patrimonioFetched.value > 0 ? patrimonioFetched.value : 433000;
      }
      const n = normalizeFirePrefs(store.state.auth.user?.fire_prefs || {});
      return n.patrimonioActual > 0 ? n.patrimonioActual : 433000;
    });

    const gastoMesSugerido = computed(() => {
      if (!isAdmin.value) return 0;
      return gastoMesFetched.value > 0 ? gastoMesFetched.value : 0;
    });

    const gastoMesSugeridoLabel = computed(() =>
      isAdmin.value && gastoMesFetched.value > 0 ? 'Último año natural' : ''
    );

    onMounted(async () => {
      if (!isAdmin.value) return;
      cargandoExternos.value = true;
      errorExternos.value = '';
      const errores = [];
      try {
        const [resEvol, resAno] = await Promise.allSettled([
          axios.get('/api/evolucion/resumen'),
          axios.get('/api/gastos/resumen/ultimo-ano-natural')
        ]);
        if (resEvol.status === 'fulfilled') {
          const pasado = resEvol.value.data.pasado || [];
          const hoy = pasado[0];
          if (hoy && hoy[2]) {
            const v = parseEuroValor(hoy[2]);
            if (v > 0) patrimonioFetched.value = v;
          }
        } else {
          errores.push('Evolución (' + (resEvol.reason?.response?.data?.error || 'error') + ')');
        }
        if (resAno.status === 'fulfilled') {
          const total = Number(resAno.value.data?.totalNumero) || 0;
          if (total > 0) {
            // Gasto mensual estimado = total gastos último año natural / 12
            gastoMesFetched.value = total / 12;
          }
        } else {
          errores.push('Resumen último año natural (' + (resAno.reason?.response?.data?.error || 'error') + ')');
        }
        if (errores.length > 0) {
          errorExternos.value = 'No se pudieron cargar algunos datos: ' + errores.join(', ') + '. Se usarán valores por defecto.';
        }
      } catch (e) {
        errorExternos.value = e.response?.data?.error || 'Error inesperado al cargar datos externos.';
      } finally {
        cargandoExternos.value = false;
      }
    });

    return {
      isAdmin,
      cargandoExternos,
      errorExternos,
      patrimonioActivo,
      gastoMesSugerido,
      gastoMesSugeridoLabel,
      firePrefsRaw
    };
  }
};
</script>
